import { useEffect, useRef, useState } from 'react';
import { PILLARS, SUBSECTIONS, type PillarId } from '../constants';
import { LOCALE_META, LOCALES, navLabel, type Locale } from '../i18n';
import { useSettings } from '../settings';
import type { RegionSummary } from '../types';
import { dotClass } from '../lib/region';
import { navigate } from '../lib/router';
import { CloseIcon, GlobeIcon, MenuIcon } from './icons';
import WalletButton from './WalletButton';

export type ViewMode = 'classic' | 'cosmos';

interface HeaderProps {
  regions: RegionSummary[];
  activeRegion: string | null;
  onRegionChange: (region: string | null) => void;
  pillar: PillarId;
  subsection: string | null;
  onSelect: (pillar: PillarId, subsection: string | null) => void;
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
}

function useLockBody(open: boolean) {
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);
}

function SectionTitle({ children }: { children: string }) {
  return (
    <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-dim">{children}</p>
  );
}

export default function Header({
  regions,
  activeRegion,
  onRegionChange,
  pillar,
  subsection,
  onSelect,
  mode,
  onModeChange,
}: HeaderProps) {
  const { t, locale, setLocale, theme, size, reduceMotion, setTheme, setSize, setReduceMotion } =
    useSettings();

  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useLockBody(menuOpen || settingsOpen);

  useEffect(() => {
    if (!langOpen) return;
    const onDown = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [langOpen]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      setSettingsOpen(false);
      setLangOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pick = (nextPillar: PillarId, nextSubsection: string | null) => {
    onSelect(nextPillar, nextSubsection);
    onModeChange('classic');
    setMenuOpen(false);
    navigate('/');
  };

  const today = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

  const subsections = SUBSECTIONS[pillar];

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-rule bg-surface/95 backdrop-blur">
        <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-6">
          {/* Row 1: masthead */}
          <div className="flex h-16 items-center gap-3">
            <a
              href="/"
              onClick={(event) => {
                event.preventDefault();
                pick('news', null);
              }}
              className="flex shrink-0 items-center gap-3"
            >
              <span className="font-serif text-[1.7rem] font-black leading-none tracking-[-0.03em] text-ink">
                III NEWS
              </span>
              <span className="hidden font-mono text-[8px] uppercase leading-tight tracking-[0.24em] text-ink-dim sm:block">
                AI News
                <br />
                Syndicate
              </span>
            </a>

            <span className="ml-3 hidden font-mono text-[9px] uppercase tracking-[0.14em] text-ink-dim lg:block">
              {today}
            </span>

            <div className="ml-auto flex items-center gap-2">
              {/* Edition / desk switcher */}
              <label className="relative hidden items-center gap-1 sm:flex">
                <span className="sr-only">{t('desks')}</span>
                <select
                  value={activeRegion ?? ''}
                  onChange={(event) => onRegionChange(event.target.value || null)}
                  className="appearance-none rounded-none border border-rule bg-transparent py-1 pl-2 pr-6 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft focus:border-rule-strong focus:outline-none"
                >
                  <option value="">{t('allDesks')}</option>
                  {regions.map((region) => (
                    <option key={region.region_color} value={region.region_color}>
                      {region.region}
                    </option>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-1.5 text-[8px] text-ink-dim">▾</span>
              </label>

              <div ref={langRef} className="relative">
                <button
                  type="button"
                  onClick={() => setLangOpen((open) => !open)}
                  aria-expanded={langOpen}
                  className="flex items-center gap-1 border border-rule px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft hover:border-rule-strong"
                >
                  {LOCALE_META[locale].short}
                  <span className="text-[8px] text-ink-dim">▾</span>
                </button>

                {langOpen && (
                  <ul className="absolute right-0 top-full z-50 mt-1 w-44 border border-rule bg-surface-raised py-1 shadow-xl">
                    {LOCALES.map((code: Locale) => (
                      <li key={code}>
                        <button
                          type="button"
                          onClick={() => {
                            setLocale(code);
                            setLangOpen(false);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${
                            code === locale ? 'text-ink' : 'text-ink-muted hover:text-ink'
                          }`}
                        >
                          <span>{LOCALE_META[code].native}</span>
                          <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-dim">
                            {LOCALE_META[code].short}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                title={t('settings')}
                aria-label={t('settings')}
                className="hidden border border-rule px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft hover:border-rule-strong sm:block"
              >
                {t('settings')}
              </button>

              <div className="hidden sm:block">
                <WalletButton />
              </div>

              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label={t('menu')}
                className="flex items-center gap-1.5 border border-rule px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft hover:border-rule-strong"
              >
                <MenuIcon className="size-3.5" />
                <span className="hidden sm:inline">{t('menu')}</span>
              </button>
            </div>
          </div>

          {/* Row 2: the five pillars */}
          <nav aria-label={t('sections')} className="no-scrollbar -mb-px flex items-stretch gap-5 overflow-x-auto">
            {PILLARS.map((item) => {
              const active = item.id === pillar;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => pick(item.id, null)}
                  aria-current={active ? 'page' : undefined}
                  className={`relative shrink-0 pb-2 pt-1 text-[13px] font-semibold tracking-[0.01em] transition-colors sm:text-sm ${
                    active ? 'text-ink' : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {navLabel(locale, item.id)}
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 -bottom-px h-[3px]"
                    style={{ backgroundColor: active ? item.color : 'transparent' }}
                  />
                </button>
              );
            })}
          </nav>
        </div>

        {/* Row 3: subsections of the active pillar */}
        <div className="border-t border-rule/70">
          <div className="mx-auto flex w-full max-w-[1240px] items-center gap-3 px-4 sm:px-6">
            <div className="no-scrollbar flex flex-1 items-center gap-4 overflow-x-auto py-2">
              <button
                type="button"
                onClick={() => pick(pillar, null)}
                className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] ${
                  subsection === null ? 'text-ink' : 'text-ink-dim hover:text-ink-soft'
                }`}
              >
                {t('allDesks')}
              </button>
              {subsections.map((slug) => (
                <button
                  key={slug}
                  type="button"
                  onClick={() => pick(pillar, slug)}
                  aria-current={slug === subsection ? 'page' : undefined}
                  className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] ${
                    slug === subsection ? 'text-ink' : 'text-ink-dim hover:text-ink-soft'
                  }`}
                >
                  {navLabel(locale, slug)}
                </button>
              ))}
            </div>

            <div className="hidden items-center gap-2 py-1.5 md:flex">
              <button
                type="button"
                onClick={() => onModeChange('classic')}
                className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.14em] ${
                  mode === 'classic'
                    ? 'border-rule-strong text-ink'
                    : 'border-rule text-ink-dim hover:text-ink-soft'
                }`}
              >
                {t('classic')}
              </button>
              <button
                type="button"
                onClick={() => onModeChange('cosmos')}
                className={`flex items-center gap-1 border px-2 py-1 font-mono text-[9px] uppercase tracking-[0.14em] ${
                  mode === 'cosmos'
                    ? 'border-rule-strong text-ink'
                    : 'border-rule text-ink-dim hover:text-ink-soft'
                }`}
              >
                <GlobeIcon className="size-3" />
                {t('cosmos')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Menu drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label={t('close')}
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <aside className="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col border-l border-rule bg-surface-raised">
            <div className="flex items-center justify-between border-b border-rule px-5 py-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                {t('menu')}
              </span>
              <button type="button" onClick={() => setMenuOpen(false)} aria-label={t('close')}>
                <CloseIcon className="size-4 text-ink-muted hover:text-ink" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5">
              <SectionTitle>{t('sections')}</SectionTitle>
              <ul className="mb-6 space-y-1">
                {PILLARS.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => pick(item.id, null)}
                      className="flex w-full items-center gap-2 py-1 text-left"
                    >
                      <span className="size-2" style={{ backgroundColor: item.color }} aria-hidden="true" />
                      <span className={item.id === pillar ? 'text-ink' : 'text-ink-soft'}>
                        {navLabel(locale, item.id)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <SectionTitle>{navLabel(locale, pillar)}</SectionTitle>
              <ul className="mb-6 space-y-1">
                {subsections.map((slug) => (
                  <li key={slug}>
                    <button
                      type="button"
                      onClick={() => pick(pillar, slug)}
                      className={`py-1 text-left text-sm ${
                        slug === subsection ? 'text-ink' : 'text-ink-muted hover:text-ink-soft'
                      }`}
                    >
                      {navLabel(locale, slug)}
                    </button>
                  </li>
                ))}
              </ul>

              <SectionTitle>{t('desks')}</SectionTitle>
              <ul className="mb-6 space-y-1">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      onRegionChange(null);
                      setMenuOpen(false);
                    }}
                    className={`flex items-center gap-2 py-1 text-sm ${
                      activeRegion === null ? 'text-ink' : 'text-ink-muted'
                    }`}
                  >
                    {t('allDesks')}
                  </button>
                </li>
                {regions.map((region) => (
                  <li key={region.region_color}>
                    <button
                      type="button"
                      onClick={() => {
                        onRegionChange(region.region_color);
                        setMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 py-1 text-sm ${
                        activeRegion === region.region_color ? 'text-ink' : 'text-ink-muted'
                      }`}
                    >
                      <span className={`size-1.5 ${dotClass(region.region_color)}`} aria-hidden="true" />
                      {region.region}
                      <span className="ml-auto font-mono text-[9px] text-ink-dim">
                        {region.article_count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <SectionTitle>{t('settings')}</SectionTitle>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setSettingsOpen(true);
                  }}
                  className="border border-rule px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft"
                >
                  {t('settings')}
                </button>
                <button
                  type="button"
                  onClick={() => onModeChange('cosmos')}
                  className="flex items-center gap-1 border border-rule px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft"
                >
                  <GlobeIcon className="size-3" />
                  {t('cosmos')}
                </button>
              </div>
            </div>

            <div className="border-t border-rule px-5 py-4">
              <WalletButton />
            </div>
          </aside>
        </div>
      )}

      {/* Settings drawer */}
      {settingsOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label={t('close')}
            onClick={() => setSettingsOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-sm overflow-y-auto border-l border-rule bg-surface-raised">
            <div className="flex items-center justify-between border-b border-rule px-5 py-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
                {t('settings')}
              </span>
              <button type="button" onClick={() => setSettingsOpen(false)} aria-label={t('close')}>
                <CloseIcon className="size-4 text-ink-muted hover:text-ink" />
              </button>
            </div>

            <div className="space-y-6 px-5 py-5">
              <div>
                <SectionTitle>{t('theme')}</SectionTitle>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setTheme('dark')}
                    aria-pressed={theme === 'dark'}
                    className={`border px-3 py-2 text-sm ${
                      theme === 'dark' ? 'border-rule-strong text-ink' : 'border-rule text-ink-muted'
                    }`}
                  >
                    {t('themeDark')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTheme('light')}
                    aria-pressed={theme === 'light'}
                    className={`border px-3 py-2 text-sm ${
                      theme === 'light' ? 'border-rule-strong text-ink' : 'border-rule text-ink-muted'
                    }`}
                  >
                    {t('themeLight')}
                  </button>
                </div>
              </div>

              <div>
                <SectionTitle>{t('textSize')}</SectionTitle>
                <div className="grid grid-cols-3 gap-2">
                  {(['sm', 'md', 'lg'] as const).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSize(value)}
                      aria-pressed={size === value}
                      className={`border py-2 ${
                        size === value
                          ? 'border-rule-strong text-ink'
                          : 'border-rule text-ink-muted'
                      }`}
                    >
                      <span
                        className={
                          value === 'sm' ? 'text-sm' : value === 'md' ? 'text-base' : 'text-lg'
                        }
                      >
                        A
                      </span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-xs text-ink-dim">{t('fontPreview')}</p>
              </div>

              <div>
                <SectionTitle>{t('reduceMotion')}</SectionTitle>
                <button
                  type="button"
                  onClick={() => setReduceMotion(!reduceMotion)}
                  aria-pressed={reduceMotion}
                  className={`border px-3 py-2 text-sm ${
                    reduceMotion ? 'border-rule-strong text-ink' : 'border-rule text-ink-muted'
                  }`}
                >
                  {reduceMotion ? t('motionOn') : t('motionOff')}
                </button>
              </div>

              <div>
                <SectionTitle>{t('language')}</SectionTitle>
                <div className="grid grid-cols-2 gap-2">
                  {LOCALES.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setLocale(code)}
                      aria-pressed={code === locale}
                      className={`border px-3 py-2 text-left text-sm ${
                        code === locale ? 'border-rule-strong text-ink' : 'border-rule text-ink-muted'
                      }`}
                    >
                      {LOCALE_META[code].native}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
